import { gql } from '@efficiently/client-core-graphql-models';

/**
 * Reads the presigned download URLs of the Image markups anchored to one target, in one request.
 *
 * The server answers **exactly one entry per requested `imageRef`, in request position** — duplicates
 * included — so a caller may index the answer positionally. `url` and `expiresAt` are `null` together
 * when the content does not exist, was deleted, or is not in `projectId` within the caller's
 * organization; a single unresolvable reference is never an error (Markup contract §1).
 *
 * `imageRefs` carries at most `MARKUP_WIRE_LIMITS.imageUrlsMaxItems` ids and duplicates count toward
 * that cap, because it bounds the argument length rather than the distinct set (contract §3.3). A
 * longer list is refused with `VALIDATION`, so chunking is the caller's job.
 *
 * Its own document with a single markup root field: `SingleMarkupRootFieldGuard` rejects a document
 * mixing this query with `markups` or a markup mutation with `VALIDATION` (contract §1), and the
 * `imageRefs` come out of the `markups` answer anyway. There is no fragment because no other document
 * selects `MarkupImageUrl` — `MarkupImageUrlsResponse` (`@efficiently/client-api-plans`) is derived
 * from this query's row, so a change to the selection set breaks the compile at its consumers.
 */
export const GET_MARKUP_IMAGE_URLS = gql(`
	query MarkupImageUrls($projectId: ID!, $target: MarkupTargetInput!, $imageRefs: [ID!]!) {
		markupImageUrls(projectId: $projectId, target: $target, imageRefs: $imageRefs) {
			imageRef
			url
			expiresAt
		}
	}
`);
